import { del, get, post, put, patch } from '@/api/client'

export interface KnowledgePhraseListResponse {
  list: KnowledgePhrase[]
  total: number
}

export function getKnowledgePhraseSettings(productId?: number) {
  const params: Record<string, unknown> = {}
  if (productId !== undefined) params.productId = productId
  return get<KnowledgePhraseSettings>('/business/knowledge-phrases/settings', { params })
}

export function saveKnowledgePhraseSettings(autoGenerateEnabled: boolean) {
  return put<KnowledgePhraseSettings>('/business/knowledge-phrases/settings', {
    autoGenerateEnabled,
  })
}

export function getKnowledgePhraseBailianConfigStatus() {
  return get<KnowledgePhraseBailianConfigStatus>('/business/knowledge-phrases/bailian-config')
}

export function checkKnowledgePhraseBailianConfig() {
  return post<KnowledgePhraseBailianConfigStatus>(
    '/business/knowledge-phrases/bailian-config/health-check',
  )
}

export function getKnowledgePhrases(keyword?: string) {
  const params: Record<string, unknown> = {}
  if (keyword) params.keyword = keyword
  return get<KnowledgePhraseListResponse>('/business/knowledge-phrases', { params })
}

export function getKnowledgePhraseGroups() {
  return get<{ list: KnowledgePhraseGroup[] }>('/business/knowledge-phrases/groups')
}

export function createKnowledgePhraseGroup(data: { name: string; sortOrder: number }) {
  return post<KnowledgePhraseGroup>('/business/knowledge-phrases/groups', data)
}

export function updateKnowledgePhraseGroup(id: number, data: { name: string; sortOrder: number }) {
  return patch<KnowledgePhraseGroup>(`/business/knowledge-phrases/groups/${id}`, data)
}

export function deleteKnowledgePhraseGroup(id: number) {
  return del(`/business/knowledge-phrases/groups/${id}`)
}

export function createKnowledgePhrase(data: {
  groupId?: number
  categoryId?: number
  title: string
  content: string
  sceneTags?: string[]
  status?: string
}) {
  return post<KnowledgePhrase>('/business/knowledge-phrases', data)
}

export function updateKnowledgePhrase(
  id: number,
  data: {
    groupId?: number
    categoryId?: number
    title: string
    content: string
    sceneTags?: string[]
    status?: string
  },
) {
  return patch<KnowledgePhrase>(`/business/knowledge-phrases/${id}`, data)
}

export function toggleKnowledgePhrase(id: number, status: 'draft' | 'active' | 'offline') {
  return post<KnowledgePhrase>(`/business/knowledge-phrases/${id}/status`, { status })
}

export function createKnowledgePhraseGenerationJob(productId?: number) {
  return post<KnowledgePhraseGenerationJob>(
    '/business/knowledge-phrases/generation-jobs',
    productId !== undefined ? { productId, timeWindowDays: 7 } : { timeWindowDays: 7 },
  )
}

export function getKnowledgePhraseGenerationJob(id: number) {
  return get<KnowledgePhraseGenerationJob>(`/business/knowledge-phrases/generation-jobs/${id}`)
}

export function retryKnowledgePhraseGenerationJob(id: number) {
  return post<KnowledgePhraseGenerationJob>(
    `/business/knowledge-phrases/generation-jobs/${id}/retry`,
  )
}

export function cancelKnowledgePhraseGenerationJob(id: number) {
  return post<KnowledgePhraseGenerationJob>(
    `/business/knowledge-phrases/generation-jobs/${id}/cancel`,
  )
}

export function retrieveKnowledgePhrases(context: string, productId?: number, limit = 5) {
  return post<{ items: KnowledgePhrase[] }>('/business/knowledge-phrases/retrieve', {
    context,
    limit,
    ...(productId !== undefined ? { productId } : {}),
  })
}

export function answerKnowledgePhrases(context: string, productId?: number, limit = 5) {
  return post<{
    replyMode: 'answer'
    suggestion: string
    items: KnowledgePhrase[]
    citations: KnowledgePhraseAnswerCitation[]
    grounded: boolean
    fallback: boolean
    fallbackReason: string
    requiresHuman: boolean
  }>('/business/knowledge-phrases/answer', {
    context,
    limit,
    ...(productId !== undefined ? { productId } : {}),
  })
}

export interface KnowledgePhraseAnswerCitation {
  chunkId: string
  phraseId: number
  version: number
  title: string
  claimKeys?: string[]
}

export function getKnowledgePhraseSyncJob(id: number) {
  return get<KnowledgePhraseSyncJob>(`/business/knowledge-phrases/sync-jobs/${id}`)
}

export function retryKnowledgePhraseSyncJob(id: number) {
  return post<KnowledgePhraseSyncJob>(`/business/knowledge-phrases/sync-jobs/${id}/retry`)
}

export function cancelKnowledgePhraseSyncJob(id: number) {
  return post<KnowledgePhraseSyncJob>(`/business/knowledge-phrases/sync-jobs/${id}/cancel`)
}

export function reconcileKnowledgePhraseSyncJobs() {
  return post<{ checked: number; enqueued: number }>(
    '/business/knowledge-phrases/sync-jobs/reconcile',
  )
}

export const KNOWLEDGE_PHRASE_CHAT_PULL_PROTOCOL_VERSION = 2

/** Boss 下发的企业话术本机聊天读取任务。 */
export interface KnowledgePhraseChatPullTask {
  id: number
  taskType?: 'chat_pull' | 'active_session_discovery'
  generationJobId: number
  companyId: number
  productId?: number
  customerId: number
  contactId: string
  wechatId: string
  windowStartAt: string
  windowEndAt: string
  afterMsgId: string
  idempotencyKey: string
}

export interface KnowledgePhraseActiveSessionDiscoveryReportPayload {
  protocolVersion: number
  deviceId: string
  idempotencyKey: string
  companyId: number
  wechatId: string
  windowStartAt: string
  windowEndAt: string
  requestId?: string
  fetchStatus: 'success' | 'failed'
  failureCode?: string
  failureSummary?: string
  sessions?: Array<{ contactId: string; lastMessageAt?: string }>
}

export interface KnowledgePhraseChatPullReportResult {
  taskId: number
  taskStatus: string
  fetchSource?: 'chatservice' | 'tablestore'
  nextRetryAt?: string
}

export interface KnowledgePhraseChatPullReportPayload {
  protocolVersion: number
  deviceId: string
  idempotencyKey: string
  companyId: number
  customerId: number
  contactId: string
  wechatId: string
  windowStartAt: string
  windowEndAt: string
  fetchStatus: 'success' | 'failed'
  failureCode?: string
  failureSummary?: string
  nextAfterId?: string
  messageCount?: number
}

export interface KnowledgePhraseCandidate {
  title?: string
  content: string
  sceneTags?: string[]
}

export interface KnowledgePhraseChatPullCandidatePayload {
  protocolVersion: number
  deviceId: string
  idempotencyKey: string
  companyId: number
  customerId: number
  contactId: string
  wechatId: string
  windowStartAt: string
  windowEndAt: string
  messageCount: number
  candidates: KnowledgePhraseCandidate[]
}

/** 领取一条由 Boss 按设备和账号范围授权的企业话术聊天读取任务。 */
export function claimKnowledgePhraseChatPullTask(
  deviceId: string,
  supportsActiveSessionDiscovery = false,
) {
  return post<KnowledgePhraseChatPullTask | null>(
    '/business/knowledge-phrases/chat-pull-tasks/claim',
    {
      deviceId,
      protocolVersion: KNOWLEDGE_PHRASE_CHAT_PULL_PROTOCOL_VERSION,
      ...(supportsActiveSessionDiscovery ? { supportsActiveSessionDiscovery: true } : {}),
    },
  )
}

/** 上报本地活跃会话发现结果；服务端会将命中的客户转换为 chat_pull_task。 */
export function reportKnowledgePhraseActiveSessionDiscovery(
  taskId: number,
  payload: KnowledgePhraseActiveSessionDiscoveryReportPayload,
) {
  return post<KnowledgePhraseChatPullReportResult>(
    `/business/knowledge-phrases/chat-pull-tasks/${taskId}/discovery-report`,
    payload,
  )
}

/** 上报本机 ChatService 的读取结果；失败时由 Boss 负责 TableStore 兜底。 */
export function reportKnowledgePhraseChatPullTask(
  taskId: number,
  payload: KnowledgePhraseChatPullReportPayload,
) {
  return post<KnowledgePhraseChatPullReportResult>(
    `/business/knowledge-phrases/chat-pull-tasks/${taskId}/report`,
    payload,
  )
}

/**
 * 提交当前内存中已脱敏、限长的联系人会话片段。
 * 该独立接口不属于 V2 claim/report；片段只用于本次 AI 清洗，服务端不得持久化。
 */
export function submitKnowledgePhraseChatPullCandidates(
  taskId: number,
  payload: KnowledgePhraseChatPullCandidatePayload,
) {
  return post<{ accepted: number; filtered: number }>(
    `/business/knowledge-phrases/chat-pull-tasks/${taskId}/candidates`,
    payload,
  )
}
